import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { action, computed, makeObservable, observable } from 'mobx';

import type { EssayDto } from '@/application/interfaces/pegasus.gateway';
import { EssayType } from '@/application/interfaces/pegasus.gateway';
import { MixpanelEssaySent, Tracking } from '@/application/utils/tracking';
import { ApplicationModel } from '@/presentation/models/application.model';
import { EssayStore } from '@/presentation/stores/essay.store';
import { StudentClassesStore } from '@/presentation/stores/studentClasses.store';

import { EssayViewModel } from '../essay/essay.vm';

const CHARACTER_BY_LINE = 115;

@ViewModel({ singleton: true })
export class WriteEssayViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable hasError = false;
  @observable essay: EssayDto;
  @observable essayInput = '';
  @observable essayTitle = '';
  @observable essayLength = 0;
  @observable essayTitleLength = 0;
  @observable lineCount = 0;
  @observable essaySent = false;
  @observable draftSent = false;
  @observable isSending = false;

  constructor(
    private readonly applicationModel: ApplicationModel,
    private readonly studentClassesStore: StudentClassesStore,
    private readonly essayModel: EssayStore,
    private readonly essayViewModel: EssayViewModel,
  ) {
    makeObservable(this);
  }

  @action
  private trackingSentEvent = (essay: any) => {
    const props: MixpanelEssaySent = {
      essayGuid: essay.essayGuid,
      essayStatus: essay.enumEssayStatus,
      essayTitle: essay.essayTitle,
      essayDescription: essay.essayDescription || '',
      essayScore: essay.essayScore || 0,
      user: essay.name,
      student_id: essay.student_id,
      email: essay.email,
      topicGuid: essay.topicGuid,
      topicName: essay.topicName,
      isHighlight: essay.isHighlight,
      studentClassGuid: essay.studentClassGuid,
      studentClassTags: essay.studentClassTags,
      studentClassTitle: essay.studentClassTitle,
    };
    Tracking.essaySent(props);
  };

  @action
  private trackinCreateEvent = (essay: any) => {
    const props: MixpanelEssaySent = {
      essayGuid: essay.essayGuid,
      essayStatus: essay.enumEssayStatus,
      essayTitle: essay.essayTitle,
      essayDescription: essay.essayDescription || '',
      essayScore: essay.essayScore || 0,
      user: essay.name,
      student_id: essay.student_id,
      email: essay.email,
      topicGuid: essay.topicGuid,
      topicName: essay.topicName,
      isHighlight: essay.isHighlight,
      studentClassGuid: essay.studentClassGuid,
      studentClassTags: essay.studentClassTags,
      studentClassTitle: essay.studentClassTitle,
    };
    Tracking.essayCreated(props);
  };

  @action
  async onInit() {
    Tracking.pageView(this.studentClassesStore.getStudentClassesTrackingInfo());
    this.getContent();
    this.initTranscribedEssay();
  }

  @action
  async onDestroy() {
    this.essayModel.setCurrentTheme(null);
    this.essayModel.transcribedEssay = '';
  }

  @action.bound
  async setEssayInput(input: string) {
    this.essayInput = input;
    this.essayLength = input.length;

    this.lineCount = Math.floor(input.length / CHARACTER_BY_LINE);
  }

  @action.bound
  async setTranscribedEssay(input: string) {
    this.essayModel.transcribedEssay = input;
  }

  @action.bound
  async setEssayTitle(input: string) {
    this.essayTitle = input;
    this.essayTitleLength = input.length;
  }

  @action.bound
  async getContent() {
    try {
      this.isLoading = true;
      this.hasError = false;
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
    }
  }

  @computed
  get currentEssayTheme() {
    return this.essayModel.currentEssayTheme;
  }

  @action.bound
  async getEssay(guid: string) {
    try {
      this.isLoading = true;
      await this.essayModel.getEssay(guid);
      this.essay = this.essayModel.currentEssay;
      if (this.essay) {
        this.setEssayTitle(this.essay.title);
        this.setEssayInput(this.essay.content);
      }
      this.essayModel.setCurrentTheme(this.essayModel.currentEssay.topic);
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action.bound
  async sentEssay() {
    try {
      this.isSending = true;

      const essay = {
        title: this.essayTitle,
        content: this.essayInput,
        status: EssayType.SENT,
        topicId: this.currentEssayTheme.id,
      };

      if (this.essay?.id) {
        const result = await this.essayModel.updateEssayDraft(essay, this.essay.id);
        if (result && result.id) {
          this.essaySent = true;
          this.isSending = false;

          //related to the trackingEvent sent Essay
          this.trackingSentEvent({
            essayGuid: result.id,
            enumEssayStatus: EssayType.SENT,
            essayTitle: this.essayTitle || '',
            essayDescription: this.essayInput,
            essayScore: 0,
            user: this.essayModel.user.name,
            studentGuid: this.essayModel.user.guid,
            email: this.essayModel.user.email,
            topicGuid: this.currentEssayTheme.id || '',
            topicName: this.currentEssayTheme.name || '',
            isHighlight: this.currentEssayTheme.isHighlight,
            studentClassGuid: this.essayModel.currentStudentClass.guid,
            studentClassTags: this.essayModel.currentStudentClass.tags,
            studentClassTitle: this.essayModel.currentStudentClass.title,
          });
        }
      } else {
        const result = await this.essayModel.createEssay(essay);

        if (result && result.id) {
          this.essaySent = true;
          this.isSending = false;

          //related to the trackingEvent sent Essay
          this.trackingSentEvent({
            essayGuid: result.id,
            enumEssayStatus: EssayType.SENT,
            essayTitle: this.essayTitle || '',
            essayDescription: this.essayInput,
            essayScore: 0,
            user: this.essayModel.user.name,
            studentGuid: this.essayModel.user.guid,
            email: this.essayModel.user.email,
            topicGuid: this.currentEssayTheme.id || '',
            topicName: this.currentEssayTheme.name || '',
            isHighlight: this.currentEssayTheme.isHighlight,
            studentClassGuid: this.essayModel.currentStudentClass.guid,
            studentClassTags: this.essayModel.currentStudentClass.tags,
            studentClassTitle: this.essayModel.currentStudentClass.title,
          });
        }
      }
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
      this.hasError = true;
      this.isSending = false;
    }
  }

  @action.bound
  async sentDraftEssay() {
    try {
      this.isSending = true;
      const essay = {
        title: this.essayTitle,
        content: this.essayInput,
        status: EssayType.DRAFT,
        topicId: this.currentEssayTheme.id,
      };

      if (this.essay?.id) {
        const result = await this.essayModel.updateEssayDraft(essay, this.essay.id);
        if (result && result.id) {
          this.draftSent = true;
          this.isSending = false;

          //related to the trackingEvent draft Essay
          this.trackinCreateEvent({
            essayGuid: result.id,
            enumEssayStatus: EssayType.DRAFT,
            essayTitle: this.essayTitle || '',
            essayDescription: this.essayInput,
            essayScore: 0,
            user: this.essayModel.user.name,
            studentGuid: this.essayModel.user.guid,
            email: this.essayModel.user.email,
            topicGuid: this.currentEssayTheme.id || '',
            topicName: this.currentEssayTheme.name || '',
            isHighlight: this.currentEssayTheme.isHighlight,
            studentClassGuid: this.essayModel.currentStudentClass.guid,
            studentClassTags: this.essayModel.currentStudentClass.tags,
            studentClassTitle: this.essayModel.currentStudentClass.title,
          });
        }
      } else {
        const result = await this.essayModel.createEssayDraft(essay);
        if (result && result.id) {
          this.draftSent = true;
          this.isSending = false;

          //related to the trackingEvent sent Essay
          this.trackinCreateEvent({
            essayGuid: result.id,
            enumEssayStatus: EssayType.DRAFT,
            essayTitle: this.essayTitle || '',
            essayDescription: this.essayInput,
            essayScore: 0,
            user: this.essayModel.user.name,
            studentGuid: this.essayModel.user.guid,
            email: this.essayModel.user.email,
            topicGuid: this.currentEssayTheme.id || '',
            topicName: this.currentEssayTheme.name || '',
            isHighlight: this.currentEssayTheme.isHighlight,
            studentClassGuid: this.essayModel.currentStudentClass.guid,
            studentClassTags: this.essayModel.currentStudentClass.tags,
            studentClassTitle: this.essayModel.currentStudentClass.title,
          });
        }
      }
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
      this.draftSent = false;
      this.isSending = false;
    }
  }

  @action.bound
  initTranscribedEssay() {
    if (this.essayModel.transcribedEssay) {
      this.setEssayInput(this.essayModel.transcribedEssay);
      this.setTranscribedEssay('');
      return;
    }
  }

  @action.bound
  setIsEssayTypeModalOpen(bool: boolean) {
    this.essayViewModel.setIsEssayTypeModalOpen(bool);
  }

  @action.bound
  setIsEssayMediaModalOpen(bool: boolean) {
    this.essayViewModel.setIsEssayMediaModalOpen(bool);
  }

  @action.bound
  setIsSendEssayMediaAgainModalOpen(bool: boolean) {
    this.essayViewModel.setIsSendEssayMediaAgainModalOpen(bool);
  }

  @computed
  get isEssayMediaModalOpen() {
    return this.essayViewModel.isEssayMediaModalOpen;
  }

  @computed
  get isEssayTypeModalOpen() {
    return this.essayViewModel.isEssayTypeModalOpen;
  }

  @computed
  get isSendEssayMediaAgainModalOpen() {
    return this.essayViewModel.isSendEssayMediaAgainModalOpen;
  }

  @computed
  get application() {
    return this.applicationModel.application;
  }
}
